// ===========================================
// SubNav — Sous-barre de navigation contextuelle (Two-tier nav)
// Apparaît sous la topbar quand l'utilisateur est dans un module
// ayant des sous-pages (Gestion, Rapports, Paramétrage, Admin).
// Style : tabs GitHub (underline rouge sur l'actif).
// ===========================================
import React, { useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PlusOutlined } from '@ant-design/icons';
import { AuthContext } from '../../../common/constants';
import { hasPerm, hasAny } from '../../../common/auth/permissions';
import { useT } from '../../../common/i18n';
import { getNavConfig } from '../navConfig';

const canSee = (currentUser, item) => {
    if (!item) return false;
    if (item.perm) return hasPerm(currentUser, item.perm);
    if (item.anyOf) return hasAny(currentUser, item.anyOf);
    return true;
};

/**
 * Pour un mégamenu donné, retourne uniquement les items "principaux"
 * (hors actions rapides) filtrés par permissions.
 */
const getMainItems = (megamenu, currentUser) => {
    if (!megamenu || !megamenu.sections) return [];
    return megamenu.sections
        .filter((s) => s.type !== 'actions')
        .flatMap((s) => s.items || [])
        .filter((it) => canSee(currentUser, it));
};

const SubNav = () => {
    const t = useT();
    const { currentUser } = useContext(AuthContext);
    const location = useLocation();
    const navigate = useNavigate();

    const { megamenus } = getNavConfig(t);

    // Détermine le module actif (mégamenu dont un `match` matche le pathname)
    const activeModule = megamenus.find((mm) =>
        (mm.matches || []).some((p) => location.pathname.startsWith(p))
    );

    // Pas dans un module avec sous-items => pas de sub-nav
    if (!activeModule) return null;

    const items = getMainItems(activeModule, currentUser);
    if (items.length === 0) return null;

    // Item actuellement sélectionné (pour calculer primaryAction)
    // Utilise `matches` si fourni, sinon fallback sur `path`
    const currentItem = items.find((it) =>
        it.matches
            ? it.matches.some((m) => location.pathname.startsWith(m))
            : location.pathname.startsWith(it.path)
    );
    const primary = currentItem?.primaryAction;
    const primaryVisible = primary && (!primary.perm || hasPerm(currentUser, primary.perm));

    return (
        <div className="sub-nav" role="navigation" aria-label={`${activeModule.label} — sous-navigation`}>
            <div className="sub-nav-inner">
                <ul className="sub-nav-links" role="menubar">
                    {items.map((item) => {
                        // Active si l'URL match l'un des `matches` (si fourni)
                        // ou commence par `path` (fallback)
                        const isActive = item.matches
                            ? item.matches.some((m) => location.pathname.startsWith(m))
                            : location.pathname.startsWith(item.path);
                        return (
                            <li key={item.key} role="none">
                                <button
                                    type="button"
                                    role="menuitem"
                                    className={`sub-nav-link ${isActive ? 'is-active' : ''}`}
                                    onClick={() => navigate(item.path)}
                                    aria-current={isActive ? 'page' : undefined}
                                >
                                    {item.icon}
                                    <span className="sub-nav-link-label">{item.label}</span>
                                </button>
                            </li>
                        );
                    })}
                </ul>

                {primaryVisible && (
                    <button
                        type="button"
                        className="sub-nav-cta"
                        onClick={() => navigate(primary.path)}
                    >
                        <PlusOutlined />
                        <span>{primary.label}</span>
                    </button>
                )}
            </div>
        </div>
    );
};

export default SubNav;
